import {
  BaseSearchFormData,
  InvoiceReportsSearchQuery,
} from '@/features/invoice_reports/types';
import {
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
} from 'ap-openapi';
import { toISODateString } from 'date-util';
import { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

const isPaymentStatus = (
  value: string
): value is SearchInvoiceReportsQueryPaymentStatus =>
  Object.values<string>(SearchInvoiceReportsQueryPaymentStatus).includes(value);

export type InvoiceReportsSearchFormData = Omit<
  BaseSearchFormData,
  'status' | 'mf_file' | 'payment_status'
> & {
  payment_date?: (string | Dayjs)[] | undefined;
  status?: SearchInvoiceReportsQueryStatus | '' | null;
  mf_file?: SearchInvoiceReportsQueryMfFile | '' | null;
  payment_status?: SearchInvoiceReportsQueryPaymentStatus | '' | null;
};

interface UseSearchFormProps {
  defaultValues?: InvoiceReportsSearchFormData;
  form: UseFormReturn<InvoiceReportsSearchFormData>;
}

// Default form values
export const getDefaultFormValues = (): InvoiceReportsSearchFormData => ({
  // Date fields in form-friendly format
  created_date: ['', ''],
  submitted_date: ['', ''],
  due_date: ['', ''],
  approved_date: ['', ''],
  book_date: ['', ''],
  payment_date: ['', ''],

  // Direct API fields - use null/'' for selects so they show placeholder (undefined is skipped by RHF reset)
  number: '',
  query: '',
  status: null,
  payment_status: null,
  ap_payee_ids: [],
  mf_file: null,
  agent_applicant_office_member_ids: [],
  viewer_ids: [],
  total_amount_from: '',
  total_amount_to: '',
  sort_fields: undefined,
  sort_orders: undefined,
  page: 1,
});

// Convert form data to API query format
export const convertFormDataToApiQuery = (
  formData: InvoiceReportsSearchFormData
): InvoiceReportsSearchQuery => {
  const query: InvoiceReportsSearchQuery = {};

  if (formData.number) query.number = formData.number;
  if (formData.query) query.query = formData.query;
  if (formData.status) query.status = formData.status || undefined;
  if (formData.payment_status && isPaymentStatus(formData.payment_status))
    query.payment_status = formData.payment_status;
  if (formData.ap_payee_ids?.length) {
    query.ap_payee_ids = formData.ap_payee_ids;
  }
  if (formData.mf_file) query.mf_file = formData.mf_file || undefined;
  if (formData.agent_applicant_office_member_ids?.length) {
    query.agent_applicant_office_member_ids =
      formData.agent_applicant_office_member_ids;
  }
  if (formData.viewer_ids?.length) {
    query.viewer_office_member_ids = formData.viewer_ids;
  }
  if (formData.total_amount_from)
    query.total_amount_from = formData.total_amount_from || undefined;
  if (formData.total_amount_to)
    query.total_amount_to = formData.total_amount_to || undefined;

  if (formData.page) query.page = formData.page;

  // Convert date ranges
  if (formData.created_date?.length === 2) {
    if (formData.created_date[0])
      query.created_at_from =
        toISODateString(formData.created_date[0]) || undefined;
    if (formData.created_date[1])
      query.created_at_to =
        toISODateString(formData.created_date[1]) || undefined;
  }
  if (formData.submitted_date?.length === 2) {
    if (formData.submitted_date[0])
      query.submitted_at_from =
        toISODateString(formData.submitted_date[0]) || undefined;
    if (formData.submitted_date[1])
      query.submitted_at_to =
        toISODateString(formData.submitted_date[1]) || undefined;
  }
  if (formData.due_date?.length === 2) {
    if (formData.due_date[0])
      query.due_date_from = toISODateString(formData.due_date[0]) || undefined;
    if (formData.due_date[1])
      query.due_date_to = toISODateString(formData.due_date[1]) || undefined;
  }
  if (formData.approved_date?.length === 2) {
    if (formData.approved_date[0])
      query.approved_at_from =
        toISODateString(formData.approved_date[0]) || undefined;
    if (formData.approved_date[1])
      query.approved_at_to =
        toISODateString(formData.approved_date[1]) || undefined;
  }
  if (formData.book_date?.length === 2) {
    if (formData.book_date[0])
      query.book_date_from =
        toISODateString(formData.book_date[0]) || undefined;
    if (formData.book_date[1])
      query.book_date_to = toISODateString(formData.book_date[1]) || undefined;
  }
  if (formData.payment_date?.length === 2) {
    if (formData.payment_date[0])
      query.payment_date_from =
        toISODateString(formData.payment_date[0]) || undefined;
    if (formData.payment_date[1])
      query.payment_date_to =
        toISODateString(formData.payment_date[1]) || undefined;
  }
  if (formData?.sort_fields && formData?.sort_orders) {
    query.sort_fields = formData?.sort_fields;
    query.sort_orders = formData?.sort_orders;
  }

  return query;
};

export const useSearchForm = ({
  form,
}: Omit<UseSearchFormProps, 'defaultValues'>) => {
  const handleReset = useCallback(
    (defaultToCurrentOfficeMember?: boolean, currentOfficeMemberId?: string) =>
      () => {
        const defaultValues = getDefaultFormValues();
        if (defaultToCurrentOfficeMember && currentOfficeMemberId) {
          defaultValues.agent_applicant_office_member_ids = [
            currentOfficeMemberId,
          ];
        }
        form.reset(defaultValues);
        form.clearErrors();
      },
    [form]
  );

  return {
    handleReset,
  };
};
